import { IUsersOperations } from "./IUsersOperations";
import { Observable } from "rxjs/Observable";
import { IUser, IGroup, IRole, IResult, IOrganisation } from "../../../../model/ModelTypes";
import { IDataStore, DatabaseKey } from "../../../../repository/IDataStore";
import { IAdminApi,
    EditUsersRequest, EditUsersResponse, DeleteUsersResponse, DeleteResultRequest, EditUserItem } from "../../AdminApi";
import { isNullOrUndefined } from "util";
import { Observer, BehaviorSubject } from "rxjs";
import { RegisterRequest, EditUserRequest } from "../../../IApi";
import { catchError, first, map } from "rxjs/operators";
import { of } from 'rxjs/observable/of';
import _ = require("lodash");

export class UsersOperations implements IUsersOperations {
    private dataStore: IDataStore;
    private adminApi: IAdminApi;
    private cachedUsers: IUser[];
    private usersSubject: BehaviorSubject<IUser[]>;

    constructor(dataStore: IDataStore, api: IAdminApi, organisation: IOrganisation, users: IUser[]) {
        this.dataStore = dataStore;
        this.adminApi = api;
        this.cachedUsers = users;
        this.usersSubject = new BehaviorSubject(this.cachedUsers);
        this.dataStore
        .getDocuments<Observable<IUser[]>>(DatabaseKey.users)
        .flatMap( value => value)
        .subscribe(
            updatedUsers => {
                this.cachedUsers = updatedUsers;
                this.usersSubject.next(updatedUsers);
            },
            error => {
                throw error; 
            }
        );
    }
    getAllUsers(includePublic: boolean, errorCallback?: (error: Error) => void|undefined): Observable<IUser[]> {
        return this.usersSubject
        .pipe(
            first(),
            map(item => {
                let username = item.filter(user => user.username);
                if (!isNullOrUndefined(username)) {
                    return item.filter(user => user.username);
                } else {
                    return item;
                }
            })    
        );
    }
    observeUsers(includePublic: boolean, errorCallback?: (error: Error) => void|undefined): Observable<IUser[]> {
        return this.usersSubject
        .pipe(
            map(item => {
                let username = item.filter(user => user.username);
                if (!isNullOrUndefined(username)) {
                    return item.filter(user => user.username);
                } else {
                    return item;
                }
            })    
        );
    }
    editUser(request: EditUserRequest): Observable<IUser> {
        let updatedUser: IUser;
        return this
            .adminApi
            .editUser(request)
            .flatMap( user => {
                updatedUser = user;
                let userIndex = this.cachedUsers.findIndex( item => item.id === user.id );
                if (userIndex > -1) {
                    this.cachedUsers.splice(userIndex, 1);
                }
                this.cachedUsers.push(user);
                return this.dataStore.insertDocument(this.cachedUsers, DatabaseKey.users);
            }).map( () => updatedUser);
    }
    getApi(): IAdminApi {
        return this.adminApi;
    }
    editUsers(users: IUser[]): Observable<EditUsersResponse> {
        let response: EditUsersResponse;
        return this
            .createEditUsersRequest(users)
            .flatMap((value: EditUsersRequest) => {
                return this.adminApi.editUsers(value);
            })
            .flatMap( (value: EditUsersResponse) => {
                response = value;
                return this.updateStoredUsers(users);
            }).map( () => {
                return response;
            });
            
    }
    deleteUsers(users: IUser[]): Observable<DeleteUsersResponse> {
        let output: DeleteUsersResponse;
        return this.dataStore
        .getDocuments<Observable<IUser[]>>(DatabaseKey.users)
        .flatMap( value => value)
        .first()
        .flatMap( storedUsers => {
            this.cachedUsers = storedUsers;
            let userIds: string[] = users.map( item => {
                if (!isNullOrUndefined(item.id)
                    && this.cachedUsers.findIndex(i => i.id === item.id) > -1) {
                    return item.id;
                } else {
                    return "";
                }
            } );
            userIds = userIds.filter(item => item !== "");
            if (userIds.length > 0) {
                return this.adminApi.deleteUsers(userIds);
            } else {
                throw new Error("Invalid User ID");
            }
            
        })
        .flatMap( (response: DeleteUsersResponse) => {
            output = response;
            let deletedIds = response.success.map(item => item.userId);
            deletedIds.forEach( id => {
                let userIndex = this.cachedUsers.findIndex( item => item.id === id);
                if (userIndex > -1) {
                    this.cachedUsers.splice(userIndex, 1);
                }
            });
            return this.dataStore.insertDocument(this.cachedUsers, DatabaseKey.users);
        }).map( () => output);
    }

    addUsers(organisationCode: string, requests: RegisterRequest[])
    : Observable<{result: Array<{ request: RegisterRequest, withError: boolean, message: string, user: IUser}>}> {
        let output: {result: Array<{ request: RegisterRequest, withError: boolean, message: string, user: IUser}>};
        return this.dataStore
        .getDocuments<Observable<IUser[]>>(DatabaseKey.users)
        .flatMap( value => value)
        .first()
        .flatMap( storedUsers => {
            this.cachedUsers = storedUsers;
            if (!this.usernamesValid(organisationCode, requests)) {
                throw new Error("1 or more user names are already in use!");
            }
            if (!this.emailsValid(organisationCode, requests)) {
                throw new Error("1 or more emails are already used");    
            }
            return Observable.forkJoin(
                requests.map(
                    item => this.adminApi.registerAccount(item)
                            .map(registerResult => {
                                return {
                                    request: item,
                                    withError: false,
                                    message: registerResult.response,
                                    user: registerResult.user
                                };
                            })
                            .pipe(catchError( (error: Error) => {
                                return of({
                                    request: item,
                                    withError: true,
                                    message: error.message,
                                    user: null
                                });
                            }))
                )
            );
        })
        .flatMap( item => {
            output = {
                result: item
            };
            item.map(x => {
                if (!isNullOrUndefined(x.user)) {
                    this.cachedUsers.push(x.user);
                }
            });
            return this.dataStore.insertDocument(this.cachedUsers, DatabaseKey.users);
        })
            .map( () => {
            return this.getAllUsers( false , (error) => {throw error; } );
        })
        .map( () => output);
        
    }
    addUsersFromCSV(organisationId: string, organisationCode: string, csvData: string):
    Observable<{result: Array<{ request: RegisterRequest, withError: boolean, message: string}>}> {
        let requests: RegisterRequest[] = [];
        let lines = csvData.split("\n");
        let headers = 
        "name,username,email_address,password,role,group,access_code".split(",");
        lines.slice(1, lines.length).forEach( line => {
            let currentLine = line.split(",");
            let request: RegisterRequest = {
                organisation_code: organisationCode,
                organisation_id: organisationId
            };
            headers.forEach( (header, index) => {
                request[header] = currentLine[index];
            });
            request.confirmPassword = request.password;
            requests.push(request); 
        });
        return this.adminApi.getOrganisationDetails(organisationCode)
        .map(
            organisation => {
                let roles = organisation.roles;
                let groups = organisation.groups;
                requests.forEach( request => {
                    if (!isNullOrUndefined(roles) && !isNullOrUndefined(request.role)) {
                        request.role = this.getRoleId(roles, request.role);
                    }
                    if (!isNullOrUndefined(groups) && !isNullOrUndefined(request.group)) {
                        request.group = this.getGroupId(groups, request.group);
                    }
                });
                return requests;
            }
        ).flatMap( finalRequests => this.addUsers(organisationCode, finalRequests));
        
    }

    deleteUserResults(userId: string, results: IResult[]): Observable<IResult[]> {
        let user: IUser;
        return this.dataStore.getDocuments<Observable<IUser[]>>(DatabaseKey.users)
        .flatMap( item => item)
        .first()
        .map( item => {
            this.cachedUsers = item;
            let userResult = this.cachedUsers.find( cachedUser => cachedUser.id === userId);
            if (!isNullOrUndefined(userResult)) {
                user = userResult;
                if (!isNullOrUndefined(user.results)) {
                    let userResults: IResult[] = user.results;
                    let resultsValid = results.some( result => {
                        let foundResult = userResults.find( resultItem => {
                            return resultItem.course === result.course &&
                            resultItem.module === result.module &&
                            resultItem.submit_time === result.submit_time;
                        });
                        if (!isNullOrUndefined(foundResult)) {
                            return true;
                        } else {
                            return false;
                        }
                    });

                    if (resultsValid) {
                        return results;
                    }
                }
            }
            throw new Error("1 or more are results invalid");
        })
        .flatMap( checkedResults => {
           let requests: DeleteResultRequest[] = checkedResults.map( (result) => {
               return {
                   userId: user.id,
                   courseCode: result.course,
                   moduleCode: result.module,
                   submitTime: result.submit_time
               };
           });
           let organisationCode: string = "";
           if (!isNullOrUndefined(user.organisation)) {
               organisationCode = user.organisation;
           }
           return Observable.forkJoin(
               requests.map( item => this
                .adminApi
                .deleteUserResult(organisationCode, item))
           );
        })
        .map( response => results);
        
    }
    private getRoleId(roles: IRole[], roleName: string): string {
        let roleIndex = roles.findIndex( role => role.name === roleName);
        if (roleIndex > -1) {
            let id = roles[roleIndex].id;
            if (!isNullOrUndefined(id)) {
                return id;
            }
        }
        return "";
    }

    private getGroupId(groups: IRole[], groupName: string): string {
        let groupIndex = groups.findIndex( role => role.name === groupName);
        if (groupIndex > -1) {
            let id = groups[groupIndex].id;
            if (!isNullOrUndefined(id)) {
                return id;
            }
        }
        return "";
    }

    private usernamesValid(organisationCode: string, requests: RegisterRequest[]): boolean {
        let usedUsernames = 
            this.cachedUsers.map(item => {
                if (!isNullOrUndefined(item.username)) {
                    return item.username.replace(`${organisationCode.toUpperCase()}-`, "");
                } else {
                    return "";
                }
            });
        usedUsernames = usedUsernames.filter(item => item !== "");
        let newUsernames = requests.map( item => {
                if (!isNullOrUndefined(item.username)) {
                    return item.username.replace(`${organisationCode.toUpperCase()}-`, "");
                } else {
                    return "";
                }
            });
        newUsernames = newUsernames.filter(item => item !== "");
        return !newUsernames.some( item => {
            return usedUsernames.indexOf(item) > -1 && newUsernames.indexOf(item) > -1;
        });
    }
    private emailsValid(organisationCode: string, requests: RegisterRequest[]): boolean {
        let usedemails = 
            this.cachedUsers.map(item => {
                if (!isNullOrUndefined(item.email) && !isNullOrUndefined(item.username)) {
                    return item.email;
                } else {
                    return "";
                }
            });
        usedemails = usedemails.filter(item => item !== "");
        let newEmails = requests.map( item => {
                if (!isNullOrUndefined(item.email_address)) {
                    return item.email_address;
                } else {
                    return "";
                }
            });
        newEmails = newEmails.filter(item => item !== "");
        return !newEmails.some( item => {
            return usedemails.indexOf(item) > -1 ;
        });
    }
    private updateStoredUsers(users: IUser[]): Observable<Observable<IUser[]>> {
        users.forEach( modifiedUser => {
            let userIndex = this.cachedUsers.findIndex( item => item.id === modifiedUser.id);
            if (userIndex > -1) {
                this.cachedUsers[userIndex] = modifiedUser;
            }
        });
        return this.dataStore
        .insertDocument(this.cachedUsers, DatabaseKey.users);
    }
    private getRemovedGroups(modifiedUser: IUser): IGroup[] {
        let groups: IGroup[] = [];
        let originalUser = this.cachedUsers.find( item => item.id === modifiedUser.id);
        if (!isNullOrUndefined(originalUser)) {
            if (!isNullOrUndefined(originalUser.groups)) {
                originalUser.groups.forEach(
                    group => {
                        if (!isNullOrUndefined(modifiedUser.groups)) {
                            const groupIndex = modifiedUser.groups.findIndex( item => item.id === group.id);
                            if (groupIndex === -1) {
                                groups.push(group);
                            }
                        }
                    }
                );
            }
        } else {
            throw new Error("1 or more User Ids are invalid");
        }
        return groups;
    }
    private getAddedGroups(modifiedUser: IUser): IGroup[] {
        let groups: IGroup[] = [];
        let originalUser = this.cachedUsers.find( item => item.id === modifiedUser.id);
        if (!isNullOrUndefined(modifiedUser.groups)) {
            modifiedUser.groups.forEach( 
                group => {
                    if (!isNullOrUndefined(originalUser)) {
                        if (!isNullOrUndefined(originalUser.groups)) {
                            const groupIndex = originalUser.groups.findIndex( item => item.id === group.id);
                            if (groupIndex === -1) {
                                groups.push(group);
                            }
                        } else {
                            groups = modifiedUser.groups;
                        }
                    }
                }
            );
        }
        return groups;
    }

    private createEditUsersRequest(users: IUser[]): Observable<EditUsersRequest> {
        const createRequest = (): EditUsersRequest => {
            let request: EditUsersRequest = { users: [] };
            users.forEach(
                modifiedUser => {
                    let userIndex = this.cachedUsers.findIndex(item => item.id === modifiedUser.id);
                    
                    if (userIndex > -1) {
                        let originalUser = this.cachedUsers[userIndex];
                        let requestItem: EditUserItem = {};
                        requestItem.user_id = modifiedUser.id.toString();
                        if (modifiedUser.status !== originalUser.status) {
                            requestItem.new_status = modifiedUser.status;
                        }
                        if (modifiedUser.name !== originalUser.name) {
                            requestItem.new_name = modifiedUser.name;
                        }
                        if (modifiedUser.password !== originalUser.password) {
                            requestItem.new_password = modifiedUser.password;
                            requestItem.new_confirmPassword = modifiedUser.password;
                        }
                        if (!_.isEqual(modifiedUser.groups, originalUser.groups)) {
                            requestItem.add_groups = this.getAddedGroups(modifiedUser);
                            requestItem.remove_groups = this.getRemovedGroups(modifiedUser);
                        }
                        if (!_.isEqual(modifiedUser.role, originalUser.role)) {
                            requestItem.new_role = modifiedUser.role;
                        }
                        if (modifiedUser.is_admin !== originalUser.is_admin) {
                            requestItem.is_admin = modifiedUser.is_admin;
                        }
                        request.users.push(requestItem);
                    } else {
                        throw new Error("Invalid User");
                    }
                }
            );
            return request;
        };
        return Observable.create( (observer: Observer<EditUsersRequest>) => {
            this.dataStore
            .getDocuments<Observable<IUser[]>>(DatabaseKey.users)
            .flatMap( value => value)
            .first()
            .subscribe(
                storedUsers => {
                    this.cachedUsers = storedUsers;
                    try {
                        observer.next(createRequest());
                    } catch (error) {
                        observer.error(error);
                    } finally {
                        observer.complete();
                    }
                }
                ,
                error => {
                    observer.error(error);
                    observer.complete();
                }
            );
            
        });
        
    }
}


// WEBPACK FOOTER //
// ./src/utility/admin/UsersControls/UsersOperations/UsersOperations.ts